import { LogOut, Moon, Sun } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { useTheme } from '@/theme/ThemeProvider'
import { apiClient } from '@/api/client'
import type { Me } from '@/api/types'

function initials(me: Me | null): string {
  if (!me) return '?'
  const source = me.email ?? me.user_id
  return source.slice(0, 2).toUpperCase()
}

export function UserMenu({ me }: { me: Me | null }) {
  const { mode, setMode } = useTheme()
  const { state, isMobile } = useSidebar()
  const collapsed = state === 'collapsed' && !isMobile

  const handleLogout = async () => {
    try {
      const { redirect_url } = await apiClient.logout()
      window.location.href = redirect_url
    } catch {
      window.location.href = '/api/auth/login'
    }
  }

  const toggleMode = () => setMode(mode === 'dark' ? 'light' : 'dark')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors',
            'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            collapsed && 'justify-center px-1',
          )}
        >
          <Avatar className="h-8 w-8 border border-sidebar-border">
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
              {initials(me)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{me?.email ?? me?.user_id ?? 'Guest'}</span>
              <span className="truncate text-xs capitalize text-sidebar-foreground/60">{me?.role ?? '—'}</span>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="truncate text-sm">{me?.email ?? me?.user_id}</span>
            <span className="text-xs font-normal capitalize text-muted-foreground">{me?.role}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={toggleMode}>
          {mode === 'dark' ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
          {mode === 'dark' ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
